<template>
  <div
    class="link"
    :class="{ shrink: !expand, active: isCurrent }"
    @click="onClick"
    @mouseover="onMouseOver"
    @mouseleave="onMouseLeave"
  >
    <img v-if="!expand" class="sidemenu-icon" :src="icon" :alt="text" />
    <transition name="fade-fast">
      <span class="link-text" :class="{ shrink: !expand }">
        {{ text }}
      </span>
    </transition>

    <div v-if="!expand && isMouseHover" class="float-menu">
      <span class="text">{{ text }}</span>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { Route } from "vue-router";

interface Data {
  isCurrent: boolean;
  isMouseHover: boolean;
}

interface Methods {
  onClick: () => void;
  onMouseOver: () => void;
  onMouseLeave: () => void;
}

interface Props {
  link: string;
  text: string;
  icon: string;
  novelId: string;
  expand: boolean;
  activeRoutes?: string[];
}

function isRouteActive(routeName: string | null | undefined, link: string, activeRoutes: string[] = []): boolean {
  if (!routeName) return false;
  if (routeName === link) return true;
  return activeRoutes.includes(routeName);
}

export default Vue.extend<Data, Methods, unknown, Props>({
  data() {
    return {
      isCurrent: isRouteActive(this.$route.name, this.link, this.activeRoutes),
      isMouseHover: false,
    };
  },
  props: {
    link: String,
    text: String,
    icon: String,
    novelId: String,
    expand: {
      type: Boolean,
      default: true,
    },
    activeRoutes: {
      type: Array as () => string[],
      default: () => [],
    },
  },
  methods: {
    onClick() {
      if (!this.isCurrent) {
        this.$router.push({ name: this.link, params: { novelId: this.novelId } });
      }
    },
    onMouseOver() {
      this.isMouseHover = true;
    },
    onMouseLeave() {
      this.isMouseHover = false;
    },
  },
  watch: {
    $route(to: Route, _from: Route) {
      this.isCurrent = isRouteActive(to.name, this.link, this.activeRoutes);
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.link {
  @include hover-overlay;
  display: grid;
  align-items: center;
  grid-template-columns: 26px 12px 1fr;
  margin: auto;
  padding: 12px 16px 12px 16px;
  text-align: left;
  text-decoration: none;
  font-size: var(--font-size-md);
  font-family: "Courier New", Courier, monospace;
  line-height: initial;
  color: var(--color-text-normal);
  white-space: nowrap;
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &.shrink {
    grid-template-columns: 1fr;
    padding: 16px 8px 16px 8px;
  }

  &.active {
    background-color: var(--color-surface-subtle);
    text-decoration: none;
  }

  &:hover {
    text-decoration: none;
  }

  &:visited {
    color: var(--color-text-normal);
    font-weight: var(--font-weight-bold);
  }
}

.link-text {
  grid-column: 1/3;
  user-select: none;

  &.shrink {
    display: none;
  }
}

.sidemenu-icon {
  grid-column: 1/3;
  width: 30px;
  justify-self: center;
  filter: brightness(0);

  [data-app-theme="dark"] & {
    filter: brightness(0) invert(1);
  }
}

.float-menu {
  position: fixed;
  @include hover-overlay;
  margin-left: 60px;
  font-size: var(--font-size-xs);
  background-color: var(--color-surface-subtle);
  box-sizing: border-box;
  padding: 0.5em;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
  cursor: default;
  box-shadow: var(--shadow-md);

  .text {
    font-size: 1.1em;
    font-weight: var(--font-weight-bold);
    color: var(--color-text-normal);
  }
}
</style>
